import React, { useEffect, useState } from 'react';
import { useStore, ResetMessage } from '../../store/useStore';
import AuthLayout from './AuthLayout';
import { Loader2, Eye, EyeOff, ToggleLeft as Google } from 'lucide-react';
import { useLocation } from 'react-router-dom';

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const login = useStore((state) => state.login);
  const loginWithGoogle = useStore((state) => state.loginWithGoogle);
  const [isLoading, setIsLoading] = useStore((state) => [
    state.isLoading,
    state.updateLoadingStatus
  ]);
  useStore((state) => state.isLoading);
  // const setShowSignUp = useStore((state) => state.setShowSignUp);
  const [forgotPassword, setForgotPassword] = useState(false);
  const resetPassword = useStore((state) => state.resetPassword);
  // const [resetMessage, setResetMessage] = useState<ResetMessage>();
  const [message, setMessage] = useState('');
  const [oobCode, setOobCode] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [mode, setMode] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const location = useLocation();

  useEffect(() => {
    if (!(oobCode && apiKey)) {
      if (new URLSearchParams(location.search).get('oobCode')) {
        let oobCodetmp = new URLSearchParams(location.search).get('oobCode') || "";
        let apiKeytmp = new URLSearchParams(location.search).get('apiKey') || "";
        let modetmp = new URLSearchParams(location.search).get('mode') || "";
        setOobCode(oobCodetmp);
        setApiKey(apiKeytmp);
        setMode(modetmp);
      }
    }
  }, []);

  const handleRefresh = () => {
    // Remove query parameters
    window.history.replaceState({}, document.title, window.location.pathname);
    window.location.reload();
  };

  const handleResetPassword = async () => {
    let resetMessage: ResetMessage;
    if (confirmPassword && password) {
      if (password !== confirmPassword) {
        setError('Passwords do not match');
        return;
      }
      resetMessage = await resetPassword(email, password, oobCode);

    } else {
      resetMessage = await resetPassword(email);
    }
    if (resetMessage.type == "error") {
      setError(resetMessage.message);
    } else {
      setMessage(resetMessage.message);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (email && password && !confirmPassword) {
      setError('');
      try {
        await login(email, password);
      } catch (err) {
        setError('Invalid email or password');
      }
    }
  };

  const handleGoogleLogin = (e: React.MouseEvent) => {
    e.preventDefault();
    setError('');

    loginWithGoogle().catch((err) => {
      if (err.code === 'auth/popup-blocked') {
        setError('Please allow popups for this website to sign in with Google');
      } else {
        setError('Google login failed');
      }
    });
  };

  return (
    <AuthLayout>
      <form className="space-y-6" onSubmit={handleSubmit}>
        {error && (
          <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 px-4 py-3 rounded-lg text-sm">
            {error}
          </div>
        )}

        {message && (
          <div className="bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 text-blue-700 dark:text-blue-300 px-4 py-3 rounded-lg text-sm">
            {message}
            {message.includes('successful') && (
              <button
                onClick={handleRefresh}
                className="block mt-2 text-green-600 dark:text-green-400 hover:text-green-800 dark:hover:text-green-200 font-medium"
              >
                Proceed to Sign In
              </button>
            )}
          </div>
        )}

        {mode === "resetPassword" ? (
          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-700 dark:text-gray-200">
              Password
            </label>
            <div className="mt-1 relative">
              <input
                id="password"
                name="password"
                type={showPassword ? "text" : "password"}
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="appearance-none block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-secondary focus:border-secondary bg-white dark:bg-gray-700 text-gray-900 dark:text-white pr-10"
                disabled={isLoading}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>

            <div className='mt-4'>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                Confirm Password
              </label>
              <div className="mt-1 relative">
                <input
                  id="confirmPassword"
                  name="confirmPassword"
                  type={showConfirmPassword ? "text" : "password"}
                  required
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="appearance-none block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-secondary focus:border-secondary bg-white dark:bg-gray-700 text-gray-900 dark:text-white pr-10"
                  disabled={isLoading}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>
          </div>) : (
          <div>
            <div className="flex gap-2 justify-between">
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                Email address
              </label>
              <div className="flex items-end">
                {!forgotPassword ? (
                  <button
                    type="button" // prevent accidental form submission
                    onClick={() => setForgotPassword(true)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault(); // block Enter key
                      }
                    }}
                    className="font-medium text-secondary hover:text-opacity-90 hover:underline"
                  >
                    Forgot password?
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => setForgotPassword(false)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                      }
                    }}
                    className="font-medium text-secondary hover:text-opacity-90 hover:underline"
                  >
                    Go back to login
                  </button>
                )}
              </div>

            </div>


            <div className="mt-1">
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="appearance-none block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-secondary focus:border-secondary bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                disabled={isLoading}
              />
            </div>
          </div>)}

        {!(forgotPassword || mode == "resetPassword") && (
          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-700 dark:text-gray-200">
              Password
            </label>
            <div className="mt-1 relative">
              <input
                id="password"
                name="password"
                type={showPassword ? "text" : "password"}
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="appearance-none block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-secondary focus:border-secondary bg-white dark:bg-gray-700 text-gray-900 dark:text-white pr-10"
                disabled={isLoading}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
          </div>)}

        <div>
          {!(forgotPassword || mode == "resetPassword") ? (
            <button
              type="submit"
              className="w-full flex justify-center items-center py-2 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-secondary-700 hover:bg-secondary-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-secondary disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <Loader2 className="animate-spin -ml-1 mr-2 h-4 w-4" />
                  Signing in...
                </>
              ) : (
                'Sign in'
              )}
            </button>
          ) : (
            <button
              className="w-full flex justify-center items-center py-2 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-secondary-700 hover:bg-secondary-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-secondary disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={handleResetPassword}
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <Loader2 className="animate-spin -ml-1 mr-2 h-4 w-4" />
                  {mode == "resetPassword" ? "Resetting Password" : "Sending Reset Link..."}
                </>
              ) : (
                "Reset Password"
              )}
            </button>
          )}
        </div>
        {mode !== "resetPassword" && (
          <>
            {!forgotPassword && <>
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300 dark:border-gray-600"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400">Or continue with</span>
                </div>
              </div>

              <div>
                <button
                  type="button"
                  onClick={handleGoogleLogin}
                  className="w-full flex items-center justify-center py-2 px-4 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-secondary"
                >
                  <Google className="h-5 w-5 mr-2" />
                  Google
                </button>
              </div>
            </>}

            <div className="text-sm text-center">
              <a
                type="button"
                href="/signup"
                className="font-medium text-secondary-500 hover:text-opacity-90 hover:underline"
              >
                Don't have an account? Sign up
              </a>
            </div>
          </>)}
      </form>
    </AuthLayout>
  );
};

export default Login;